import { useMemo, useRef } from "react";
import axios from "axios";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useTrialsRouter } from "../../../shared/custom-hooks/useTrialsRouter";
import { mergeStripPlotsIntoGroup, useNewTrialState } from "../legacy";
import {
  fetchSeedTrial,
  fetchSeedAutoAssignments,
  patchSeedAssignments,
  patchSeedStandard,
  type AssignmentScope,
  type SeedAutoAssignmentResponse,
  type SavedSeedProductAssignment,
  type SeedStandard,
  type LegacySeedEntry,
} from "../services/seedAssignments";
import {
  reconcileStripSeedResponse,
  validateStripSeedTrial,
  type StripSeedTrial,
} from "../services/stripSeedAssignments";

export const stripSeedTrialKey = (
  trialId: string,
  scope: AssignmentScope
): readonly string[] => [
  "stripSeedAssignmentsTrial",
  scope.orgId,
  scope.workspaceId,
  trialId,
];

export const stripSeedMutationKey = (
  trialId: string,
  scope: AssignmentScope
): readonly string[] => [
  "stripSeedAssignmentsSave",
  scope.orgId,
  scope.workspaceId,
  trialId,
];

export class StripSeedConfirmationError extends Error {
  constructor() {
    super(
      "The request may have been saved, but its response could not be " +
        "confirmed. Reload the trial before retrying."
    );
    this.name = "StripSeedConfirmationError";
  }
}

interface SaveAssignmentsVariables {
  trial: StripSeedTrial;
  products: SavedSeedProductAssignment[];
}

interface SaveStandardVariables {
  trial: StripSeedTrial;
  standard: SeedStandard;
}

interface UseStripSeedAssignmentsResult {
  trial: StripSeedTrial | undefined;
  isLoading: boolean;
  loadError: unknown;
  saving: boolean;
  scope: AssignmentScope;
  trialId: string;
  saveAssignments: (
    variables: SaveAssignmentsVariables
  ) => Promise<StripSeedTrial>;
  saveStandard: (variables: SaveStandardVariables) => Promise<StripSeedTrial>;
  loadSuggestions: () => Promise<SeedAutoAssignmentResponse>;
}

const useStripSeedAssignments = (
  enabled = true
): UseStripSeedAssignmentsResult => {
  const queryClient = useQueryClient();
  const {
    trialInfo,
    newTrialState,
    setTrialInfo,
    setNewTrialState,
    setRatesDosage,
  } = useNewTrialState();
  const { orgId, workspaceId } = useTrialsRouter();

  const trialId = trialInfo?.id ?? "";
  const activeTrialId = useRef(trialId);
  activeTrialId.current = trialId;

  const scope = useMemo<AssignmentScope>(
    () => ({
      orgId: orgId ?? "",
      workspaceId: workspaceId ?? "",
    }),
    [orgId, workspaceId]
  );

  const queryKey = useMemo(
    () => stripSeedTrialKey(trialId, scope),
    [trialId, scope]
  );

  const mutationKey = useMemo(
    () => stripSeedMutationKey(trialId, scope),
    [trialId, scope]
  );

  const query = useQuery<StripSeedTrial, Error>({
    queryKey,
    enabled:
      enabled &&
      trialId.trim() !== "" &&
      scope.orgId.trim() !== "" &&
      scope.workspaceId.trim() !== "",
    queryFn: async ({ signal }): Promise<StripSeedTrial> =>
      validateStripSeedTrial(await fetchSeedTrial(trialId, scope, signal)),
    retry: false,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
  });

  const updateCaches = (trial: StripSeedTrial): void => {
    queryClient.setQueryData(queryKey, trial);

    void queryClient.invalidateQueries({
      queryKey: ["trial", trialId],
      refetchType: "none",
    });

    void queryClient.invalidateQueries({
      queryKey: ["trialInsights"],
      refetchType: "none",
    });
  };

  const assignmentMutation = useMutation<
    StripSeedTrial,
    unknown,
    SaveAssignmentsVariables
  >({
    mutationKey,
    retry: false,
    mutationFn: async ({ trial, products }): Promise<StripSeedTrial> => {
      await queryClient.cancelQueries({ queryKey, exact: true });
      let patchSucceeded = false;

      try {
        const response = await patchSeedAssignments(trialId, products, scope);
        patchSucceeded = true;
        reconcileStripSeedResponse(trial, response);

        // The PATCH contains normalized assignments, not refreshed native
        // properties. The legacy manual screen reads those native properties.
        const fresh = validateStripSeedTrial(
          await fetchSeedTrial(trialId, scope)
        );
        if (fresh.id !== trialId) throw new StripSeedConfirmationError();
        return fresh;
      } catch (error) {
        if (patchSucceeded || !axios.isAxiosError(error)) {
          throw new StripSeedConfirmationError();
        }

        throw error;
      }
    },
    onSuccess: async (trial) => {
      const legacyKey = ["trial", trialId];
      await queryClient.cancelQueries({ queryKey: legacyKey, exact: true });
      updateCaches(trial);
      const nativePlots = trial.trial_plots ?? [];
      const mergedPlots = mergeStripPlotsIntoGroup(nativePlots, true);
      // The legacy combined array is positional. Do not lose or shift later
      // assignments when a partially assigned strip has no seed entry yet.
      mergedPlots[0].properties.seeds = {
        measure_unit: nativePlots.find((plot) => plot.properties?.seeds)
          ?.properties?.seeds?.measure_unit,
        rates_and_dosages: nativePlots.map(
          (plot) =>
            plot.properties?.seeds?.rates_and_dosages?.[0] ?? {
              product_id: null,
              variety: "",
              rate: 0,
              color: "",
              quantity: null,
              is_standard: false,
            }
        ),
      };
      const legacyTrial = { ...trial, trial_plots: mergedPlots };
      queryClient.setQueryData(legacyKey, legacyTrial);

      // Do not publish a completed save into a different trial opened while
      // the request was in flight. Keep geometry and non-seed drafts intact.
      if (activeTrialId.current !== trialId) return;
      setTrialInfo((previous: typeof trialInfo) =>
        previous?.id === trialId ? legacyTrial : previous
      );
      if (
        trialInfo?.id !== trialId ||
        newTrialState.trial_plots.length !== mergedPlots.length
      )
        return;

      setNewTrialState((previous: typeof newTrialState) => ({
        ...previous,
        trial_plots: previous.trial_plots.map((plot, index) => ({
          ...plot,
          properties: {
            ...plot.properties,
            seeds: mergedPlots[index].properties.seeds,
          },
        })),
      }));
      setRatesDosage((previous) => ({
        ...previous,
        unit_of_rate:
          mergedPlots[0].properties.seeds?.measure_unit ??
          previous.unit_of_rate,
        rates_and_dosages: mergedPlots.map((plot, groupIndex) =>
          (plot.properties.seeds?.rates_and_dosages ?? []).map(
            (seed: LegacySeedEntry, cellIndex: number) => {
              const local = previous.rates_and_dosages[groupIndex]?.[cellIndex];
              return {
                ...local,
                ...seed,
                variety: seed.variety ?? "",
                rate: seed.rate ?? 0,
                plot_id: local?.plot_id,
                collapseValue: local?.collapseValue,
              };
            }
          )
        ),
      }));
    },
  });

  const standardMutation = useMutation<
    StripSeedTrial,
    unknown,
    SaveStandardVariables
  >({
    mutationKey,
    retry: false,
    mutationFn: async ({ trial, standard }): Promise<StripSeedTrial> => {
      await queryClient.cancelQueries({ queryKey, exact: true });

      // Generic PATCH sends field-standard metadata only.
      await patchSeedStandard(trial, standard, scope);

      return {
        ...trial,
        standard_field_properties: {
          ...trial.standard_field_properties,
          ...standard,
        },
      };
    },
    onSuccess: updateCaches,
  });

  const loadSuggestions = async (): Promise<SeedAutoAssignmentResponse> =>
    await queryClient.fetchQuery({
      queryKey: [
        "stripSeedSuggestions",
        scope.orgId,
        scope.workspaceId,
        trialId,
      ],
      queryFn: async ({ signal }) =>
        await fetchSeedAutoAssignments(trialId, scope, signal),
      staleTime: 0,
      gcTime: 0,
      retry: false,
    });

  return {
    trial: query.data,
    // Cached data can be stale while React Query refreshes it on mount. Keep
    // the editor from treating that cache as its initial saved assignment set.
    isLoading: query.isFetching,
    loadError: query.error,
    saving: assignmentMutation.isPending || standardMutation.isPending,
    scope,
    trialId,
    saveAssignments: assignmentMutation.mutateAsync,
    saveStandard: standardMutation.mutateAsync,
    loadSuggestions,
  };
};

export default useStripSeedAssignments;
